/**
 * Conversao entre horario de parede e instante, para a janela da GMUD.
 *
 * CUIDADO: `wallClockToInstant` le os campos LOCAIS do Date (getFullYear,
 * getHours, ...), NAO os UTC. Isso e proposital. O DateTimePicker devolve um
 * Date cujo horario de parede e o que a pessoa digitou; nos reinterpretamos
 * esse horario de parede como sendo do fuso do tenant. Trocar para
 * getUTCHours() parece mais correto e quebra tudo.
 */

/** Offset da zona, em minutos, para um instante dado. Negativo a oeste. */
export function zoneOffsetMinutes(at: Date, tz: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    timeZoneName: 'longOffset',
  }).formatToParts(at)
  const raw = parts.find((p) => p.type === 'timeZoneName')?.value ?? 'GMT+00:00'
  const m = /GMT([+-])(\d{2}):(\d{2})/.exec(raw)
  if (!m) return 0
  const sign = m[1] === '-' ? -1 : 1
  return sign * (Number(m[2]) * 60 + Number(m[3]))
}

/**
 * Instante UTC correspondente ao horario de parede `wall` na zona `tz`.
 *
 * Duas passadas: o offset depende do instante, e o instante depende do
 * offset. A segunda passada acerta zonas com horario de verao (o Brasil nao
 * tem desde 2019, mas a lista curada inclui New_York e Lisbon).
 */
export function wallClockToInstant(wall: Date, tz: string): Date {
  const asUtc = Date.UTC(
    wall.getFullYear(),
    wall.getMonth(),
    wall.getDate(),
    wall.getHours(),
    wall.getMinutes(),
    0,
    0,
  )
  const off1 = zoneOffsetMinutes(new Date(asUtc), tz)
  const first = new Date(asUtc - off1 * 60_000)
  const off2 = zoneOffsetMinutes(first, tz)
  return off2 === off1 ? first : new Date(asUtc - off2 * 60_000)
}

/**
 * O inverso de `wallClockToInstant`: o Date cujo horario de parede (campos
 * LOCAIS, pelo mesmo motivo de la) e o do instante `at` visto na zona `tz`.
 * Serve para pre-preencher o seletor com uma janela que ja esta gravada.
 */
export function instantToWallClock(at: Date, tz: string): Date {
  const naZona = new Date(at.getTime() + zoneOffsetMinutes(at, tz) * 60_000)
  return new Date(
    naZona.getUTCFullYear(),
    naZona.getUTCMonth(),
    naZona.getUTCDate(),
    naZona.getUTCHours(),
    naZona.getUTCMinutes(),
  )
}

/** "10/08/2026 02:00" — o instante `at` visto na zona `tz`. */
export function formatInZone(at: Date, tz: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: tz,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(at)
}

/** "America/Manaus (UTC−04:00)" */
export function zoneLabel(tz: string, at: Date = new Date()): string {
  const off = zoneOffsetMinutes(at, tz)
  const sign = off < 0 ? '−' : '+'
  const abs = Math.abs(off)
  const hh = String(Math.floor(abs / 60)).padStart(2, '0')
  const mm = String(abs % 60).padStart(2, '0')
  return `${tz} (UTC${sign}${hh}:${mm})`
}
